import { useMemo } from "react";
import { questionTypes } from "../formBuilder/formBuilderConsts";

function getHeroImage(imageTypes, images) {
    if (!images || images.length === 0 || !imageTypes) {
        return images?.[0] || null;
    }

    const priorityMap = imageTypes.reduce((acc, type) => {
        acc[type.id] = type.crHeroImagePriority;
        return acc;
    }, {});

    const sortedImages = images
        .filter(image => {
            const priority = priorityMap[image.imageTypeId];
            return typeof priority === 'number';
        })
        .sort((a, b) => {
            const priorityA = priorityMap[a.imageTypeId];
            const priorityB = priorityMap[b.imageTypeId];
            return priorityA - priorityB;
        });

    if (sortedImages.length > 0) {
        return sortedImages[0];
    }

    return images[0];
}

export const useProcessedReportData = (vehicle, answers, sections, imageTypes, generatedDate, lot, showFailuresOnly = false) => {
    return useMemo(() => {
        const metadata = {
            year: vehicle?.year || '',
            make: vehicle?.make || '',
            model: vehicle?.model || '',
            vin: vehicle?.descriptor || '',
            plate: vehicle?.assetId || '',
            mileage: vehicle?.mileage || '',
            date: generatedDate ? new Date(generatedDate).toLocaleString() : '',
            location: lot?.name || '',
        };

        const imageTypeMap = new Map((imageTypes || []).map(it => [it.id, it.name]));
        const answersMap = new Map(answers.map(a => [a.questionId, a]));
        const answerImageUrls = new Set();

        const parseAnswerValue = (answer) => {
            if (!answer || !answer.values) {
                return null;
            }

            try {
                return JSON.parse(answer.values);
            } catch {
                return answer.values;
            }
        };

        const getDisplayValue = (question, answer) => {
            const parsedValue = parseAnswerValue(answer);

            if (question.questionType === questionTypes.Image) {
                return answer?.pictureUrl ? 'Image Taken' : 'Image Not Taken';
            }

            if (parsedValue === null || parsedValue === undefined) {
                return 'Not Answered';
            }

            if (Array.isArray(parsedValue)) {
                return parsedValue.map(item => item.value || item.name).join(', ');
            }

            if (typeof parsedValue === 'object') {
                return parsedValue.value || parsedValue.name;
            }

            return String(parsedValue);
        };

        const processedSections = sections.map(section => {
            let sectionStatus = 'pass';
            const sectionImages = new Set();

            const questions = section.questions.map(q => {
                const answer = answersMap.get(q.id);
                let displayValue = 'Not Answered';

                if (answer) {
                    if (answer.dateValue) {
                        const date = new Date(answer.dateValue);
                        if (!isNaN(date.getTime())) {
                            const month = String(date.getUTCMonth() + 1).padStart(2, '0');
                            const day = String(date.getUTCDate()).padStart(2, '0');
                            const year = date.getUTCFullYear();
                            displayValue = `${month}/${day}/${year}`;
                        } else {
                            displayValue = getDisplayValue(q, answer);
                        }
                    } else {
                        displayValue = getDisplayValue(q, answer);
                    }
                }

                const parsedValue = parseAnswerValue(answer);
                let questionStatus = 'pass';

                if (answer?.pictureUrl) {
                    sectionImages.add({ url: answer.pictureUrl, type: q.question });
                    answerImageUrls.add(answer.pictureUrl);
                }

                const selectedOptions = Array.isArray(parsedValue) ? parsedValue : [parsedValue];
                if (selectedOptions.some(opt => opt && opt.isFailure)) {
                    questionStatus = 'fail';
                    sectionStatus = 'fail';
                }

                return { ...q, answer, displayValue, status: questionStatus };
            });

            return { ...section, questions, status: sectionStatus, images: Array.from(sectionImages) };
        });

        const vehicleImagesWithType = (vehicle?.images || []).map(img => ({
            ...img,
            type: imageTypeMap.get(img.imageTypeId) || 'Vehicle Image'
        }));

        const heroImageObject = getHeroImage(imageTypes, vehicleImagesWithType);
        const generalVehicleImages = vehicleImagesWithType.filter(img => !answerImageUrls.has(img.url));

        let filteredSections = processedSections;
        if (showFailuresOnly) {
            filteredSections = processedSections
                .map(section => {
                    const failedQuestions = section.questions.filter(q => q.status === 'fail');
                    if (failedQuestions.length === 0) {
                        return null;
                    }

                    const failedQuestionUrls = new Set(
                        failedQuestions
                            .map(q => answersMap.get(q.id)?.pictureUrl)
                            .filter(Boolean)
                    );

                    const filteredImages = section.images.filter(img => failedQuestionUrls.has(img.url));

                    return {
                        ...section,
                        questions: failedQuestions,
                        images: filteredImages
                    };
                })
                .filter(Boolean);
        }

        return { metadata, sections: filteredSections, heroImageObject, generalVehicleImages };

    }, [vehicle, answers, sections, imageTypes, generatedDate, lot, showFailuresOnly]);
};
